/** Hard cap so an interrupted or never-ending scroll can't leave the caller waiting forever. */
export const SCROLL_SETTLE_TIMEOUT_MS = 3000;

/** How long to wait for a smooth scroll to start before assuming there was nothing to scroll. */
export const SCROLL_START_GRACE_MS = 150;

/**
 * Frames the scroll position must hold steady before the scroll counts as finished. This also
 * acts as the settle beat before the caller opens the banner.
 */
const STABLE_FRAME_COUNT = 3;

/**
 * Resolves once the page has stopped scrolling.
 *
 * Smooth-scroll duration scales with distance, so a fixed timeout either fires mid-scroll for
 * far-down targets or stalls short ones. This watches the actual scroll position instead and
 * uses `scrollend` as a fast path where it's supported.
 */
export function waitForScrollToSettle(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return Promise.resolve();
  }

  return new Promise<void>(resolve => {
    let settled = false;
    let frameId = 0;
    let stableFrames = 0;
    let hasMoved = false;
    let lastY = window.scrollY;
    const startedAt = Date.now();

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('scrollend', finish);
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      resolve();
    };

    const checkFrame = () => {
      const currentY = window.scrollY;

      if (currentY === lastY) {
        stableFrames += 1;
      } else {
        hasMoved = true;
        stableFrames = 0;
      }
      lastY = currentY;

      const isDone = hasMoved
        ? stableFrames >= STABLE_FRAME_COUNT
        : Date.now() - startedAt >= SCROLL_START_GRACE_MS;

      if (isDone) {
        finish();
        return;
      }

      frameId = window.requestAnimationFrame(checkFrame);
    };

    window.addEventListener('scrollend', finish);
    frameId = window.requestAnimationFrame(checkFrame);
    const timeoutId = window.setTimeout(finish, SCROLL_SETTLE_TIMEOUT_MS);
  });
}
